'use client';

/**
 * Leave & Disability Page
 *
 * Provides access to FMLA, disability, and leave management
 * with privacy-focused design and anonymous inquiry options.
 */

import React, { useState } from 'react';
import {
  Briefcase,
  Calendar,
  Clock,
  Shield,
  FileText,
  MessageSquare,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  Lock,
  Eye,
  EyeOff,
  Phone,
  HelpCircle,
  Heart,
  Baby,
  Home,
  Users,
  ArrowRight,
} from 'lucide-react';
import EmployerLayout from '../layout';

interface LeaveType {
  id: string;
  name: string;
  icon: React.ElementType;
  description: string;
  eligibility: string;
  maxDuration: string;
  isPaid: boolean;
  requiresMedical: boolean;
}

const leaveTypes: LeaveType[] = [
  {
    id: 'fmla',
    name: 'FMLA Leave',
    icon: Shield,
    description: 'Federal job-protected leave for qualifying family and medical reasons',
    eligibility: '12+ months employed, 1,250+ hours worked',
    maxDuration: 'Up to 12 weeks per year',
    isPaid: false,
    requiresMedical: true,
  },
  {
    id: 'medical',
    name: 'Medical Leave',
    icon: Heart,
    description: 'Leave for your own serious health condition',
    eligibility: 'All eligible employees',
    maxDuration: 'Based on medical certification',
    isPaid: true,
    requiresMedical: true,
  },
  {
    id: 'parental',
    name: 'Parental Leave',
    icon: Baby,
    description: 'Leave for birth, adoption, or foster care placement',
    eligibility: 'All employees after 90 days',
    maxDuration: 'Up to 16 weeks',
    isPaid: true,
    requiresMedical: false,
  },
  {
    id: 'caregiver',
    name: 'Caregiver Leave',
    icon: Users,
    description: 'Leave to care for a family member with a serious health condition',
    eligibility: 'FMLA eligible employees',
    maxDuration: 'Up to 12 weeks per year',
    isPaid: false,
    requiresMedical: true,
  },
  {
    id: 'personal',
    name: 'Personal Leave',
    icon: Home,
    description: 'Unpaid leave for personal matters not covered by other policies',
    eligibility: 'Manager approval required',
    maxDuration: 'Up to 30 days',
    isPaid: false,
    requiresMedical: false,
  },
];

export default function LeavePage() {
  const [selectedLeave, setSelectedLeave] = useState<string | null>(null);
  const [anonymousMode, setAnonymousMode] = useState(false);
  const [showEligibilityCheck, setShowEligibilityCheck] = useState(false);

  const handleLearnMore = (leaveId: string) => {
    if (anonymousMode) {
      // In anonymous mode, open information without tracking
      setSelectedLeave(leaveId);
    } else {
      setSelectedLeave(leaveId);
    }
  };

  return (
    <EmployerLayout>
      <div className="max-w-6xl mx-auto">
        {/* Header with Anonymous Mode Toggle */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Leave & Disability</h1>
            <p className="text-gray-600 mt-1">Explore leave options and manage time off requests</p>
          </div>
          <div className="mt-4 md:mt-0">
            <button
              onClick={() => setAnonymousMode(!anonymousMode)}
              className={`
                inline-flex items-center px-4 py-2 rounded-lg border transition-colors
                ${anonymousMode
                  ? 'bg-green-50 border-green-200 text-green-700'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                }
              `}
            >
              {anonymousMode ? (
                <>
                  <EyeOff className="w-4 h-4 mr-2" />
                  Anonymous Mode On
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4 mr-2" />
                  Enable Anonymous Mode
                </>
              )}
            </button>
          </div>
        </div>

        {/* Anonymous Mode Notice */}
        {anonymousMode && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
            <div className="flex items-start gap-3">
              <Lock className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-green-900">Anonymous Browsing Active</h3>
                <p className="text-sm text-green-700 mt-1">
                  Your browsing is not being tracked or recorded. You can explore leave options privately.
                  To request leave or speak with HR, you'll need to disable anonymous mode.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Available PTO</p>
                <p className="text-2xl font-bold text-gray-900">15 days</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">Accrues 1.25 days/month</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Sick Leave</p>
                <p className="text-2xl font-bold text-gray-900">8 days</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <Heart className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">10 days annually</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">FMLA Eligible</p>
                <p className="text-2xl font-bold text-green-600">Yes</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <Shield className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">12 weeks available</p>
          </div>
        </div>

        {/* Leave Types Grid */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Leave Options</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {leaveTypes.map((leave) => {
              const Icon = leave.icon;
              return (
                <button
                  key={leave.id}
                  onClick={() => handleLearnMore(leave.id)}
                  className={`
                    text-left p-5 bg-white rounded-xl border transition-all
                    ${selectedLeave === leave.id
                      ? 'border-indigo-300 ring-2 ring-indigo-100'
                      : 'border-gray-200 hover:border-indigo-200 hover:shadow-md'
                    }
                  `}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <Icon className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div className="flex items-center gap-1">
                      {leave.isPaid && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">Paid</span>
                      )}
                      {leave.requiresMedical && (
                        <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">Cert Required</span>
                      )}
                    </div>
                  </div>
                  <h3 className="font-semibold text-gray-900">{leave.name}</h3>
                  <p className="text-sm text-gray-600 mt-1">{leave.description}</p>
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-500">
                      <span className="font-medium">Duration:</span> {leave.maxDuration}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected Leave Details */}
        {selectedLeave && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {leaveTypes.find(l => l.id === selectedLeave)?.name} Details
              </h2>
              <button
                onClick={() => setSelectedLeave(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">Close</span>
                &times;
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Eligibility Requirements</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    {leaveTypes.find(l => l.id === selectedLeave)?.eligibility}
                  </li>
                  {selectedLeave === 'fmla' && (
                    <>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Work at a location with 50+ employees within 75 miles
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Qualifying reason for leave
                      </li>
                    </>
                  )}
                </ul>
              </div>

              <div>
                <h3 className="font-medium text-gray-900 mb-2">How to Request</h3>
                <ol className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-5 h-5 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center text-xs font-medium">1</span>
                    Complete the leave request form
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-5 h-5 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center text-xs font-medium">2</span>
                    Submit medical certification (if required)
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-5 h-5 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center text-xs font-medium">3</span>
                    HR will review and respond within 5 business days
                  </li>
                </ol>
              </div>
            </div>

            {!anonymousMode && (
              <div className="mt-6 flex flex-wrap gap-3">
                <button className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors">
                  Request Leave
                </button>
                <button className="px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                  Download Forms
                </button>
                <button className="px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Talk to HR
                </button>
              </div>
            )}
          </div>
        )}

        {/* Confidential Resources */}
        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl border border-purple-200 p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Lock className="w-6 h-6 text-purple-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-purple-900">Confidential Resources</h2>
              <p className="text-sm text-purple-700 mt-1">
                These resources are completely confidential. Your employer will not be notified of your usage.
              </p>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <a
                  href="#eap"
                  className="flex items-center gap-3 p-4 bg-white rounded-lg border border-purple-200 hover:border-purple-300 transition-colors"
                >
                  <MessageSquare className="w-5 h-5 text-purple-600" />
                  <div>
                    <p className="font-medium text-gray-900">Employee Assistance (EAP)</p>
                    <p className="text-xs text-gray-500">24/7 confidential support</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-purple-400 ml-auto" />
                </a>
                <a
                  href="tel:1-800-555-0123"
                  className="flex items-center gap-3 p-4 bg-white rounded-lg border border-purple-200 hover:border-purple-300 transition-colors"
                >
                  <Phone className="w-5 h-5 text-purple-600" />
                  <div>
                    <p className="font-medium text-gray-900">Anonymous Hotline</p>
                    <p className="text-xs text-gray-500">1-800-555-0123</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-purple-400 ml-auto" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </EmployerLayout>
  );
}
